import React from 'react'
import Link from 'next/link'
import { ChevronRight, Home, Sparkles, ShieldCheck } from 'lucide-react'

const C = {
    PRIMARY: '#F8F246',
    SECONDARY: '#2CBBC3',
    BROWN: '#8C5826',
    ORANGE: '#FF9934',
    BG: '#fffaf5',
    BORDER: '#f0ede8',
    TEXT_DARK: '#1a1a1a',
    TEXT_MUTED: '#6b7280',
}

interface Crumb {
    label: string
    href?: string
}

interface StatItem {
    label: string
    value: string | number
    icon?: React.ComponentType<{ size?: number; className?: string }>
}

interface Props {
    title: string
    subtitle?: string
    highlight?: string
    icon?: React.ReactNode
    breadcrumbs?: Crumb[]
    badge?: string
    stats?: StatItem[]
}

const PageHeader = ({
    title,
    subtitle,
    highlight,
    icon,
    breadcrumbs,
    badge,
    stats,
}: Props) => {
    return (
        <section className="relative overflow-hidden pt-8 pb-10 md:pt-10 md:pb-14 px-4 sm:px-6 lg:px-8"
            style={{ background: 'linear-gradient(135deg, #fffaf5 0%, #fff4ea 100%)', borderBottom: '1px solid rgba(248, 242, 70, 0.4)' }}>

            {/* Background glowing ambient light */}
            <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full blur-3xl opacity-25 pointer-events-none"
                style={{ background: C.PRIMARY }} />
            <div className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full blur-3xl opacity-15 pointer-events-none"
                style={{ background: C.SECONDARY }} />

            <div className="relative max-w-7xl mx-auto space-y-5">

                {/* ── 1. Breadcrumb Trail ── */}
                {breadcrumbs && breadcrumbs.length > 0 && (
                    <nav className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-white/90 backdrop-blur-md border border-[#f0ede8] shadow-xs"
                        aria-label="Breadcrumb">
                        <Link href="/" className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-500 hover:text-[#8C5826] transition-colors">
                            <Home size={12} className="text-gray-400" />
                            <span>Home</span>
                        </Link>
                        {breadcrumbs.map((crumb, i) => (
                            <React.Fragment key={i}>
                                <ChevronRight size={11} className="text-gray-300 flex-shrink-0" />
                                {crumb.href && i < breadcrumbs.length - 1 ? (
                                    <Link href={crumb.href}
                                        className="text-[11px] font-semibold text-gray-500 hover:text-[#8C5826] transition-colors truncate max-w-[160px]">
                                        {crumb.label}
                                    </Link>
                                ) : (
                                    <span className="text-[11px] font-black text-[#8C5826] truncate max-w-[220px]">
                                        {crumb.label}
                                    </span>
                                )}
                            </React.Fragment>
                        ))}
                    </nav>
                )}

                {/* ── 2. Main Header Content ── */}
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
                    <div className="space-y-3.5 max-w-3xl">

                        {/* Eyebrow badge */}
                        {badge && (
                            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full shadow-xs bg-white border border-[#F8F246] w-fit">
                                <Sparkles size={12} style={{ color: C.BROWN }} />
                                <span className="text-[10px] font-black uppercase tracking-widest text-[#8C5826]">
                                    {badge}
                                </span>
                            </div>
                        )}

                        {/* Title with optional icon & highlight */}
                        <div className="flex items-start sm:items-center gap-4">
                            {icon ? (
                                <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-md bg-white border border-[#f0ede8]">
                                    {icon}
                                </div>
                            ) : null}
                            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 tracking-tight leading-tight flex flex-wrap items-center gap-x-3 gap-y-2">
                                <span>{title}</span>
                                {highlight && (
                                    <span className="inline-flex items-center px-3.5 py-1 rounded-2xl text-2xl sm:text-3xl md:text-4xl font-black shadow-xs align-middle"
                                        style={{ background: `${C.PRIMARY}70`, color: C.BROWN, border: `1.5px solid ${C.PRIMARY}` }}>
                                        {highlight}
                                    </span>
                                )}
                            </h1>
                        </div>

                        {/* Subtitle */}
                        {subtitle && (
                            <p className="text-sm sm:text-base text-gray-600 leading-relaxed max-w-2xl">
                                {subtitle}
                            </p>
                        )}
                    </div>

                    {/* ── 3. Optional Stats Badges ── */}
                    {stats && stats.length > 0 && (
                        <div className="flex flex-wrap items-center gap-3 flex-shrink-0">
                            {stats.map((stat, idx) => {
                                const StatIcon = stat.icon || ShieldCheck
                                return (
                                    <div key={idx}
                                        className="flex items-center gap-3 px-4 py-2.5 rounded-2xl bg-white border border-[#f0ede8] shadow-xs hover:border-[#8C5826]/20 transition-all">
                                        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ background: `${C.PRIMARY}40`, color: C.BROWN }}>
                                            <StatIcon size={16} />
                                        </div>
                                        <div>
                                            <p className="text-sm font-black text-gray-900 leading-none">{stat.value}</p>
                                            <p className="text-[10px] font-semibold text-gray-500 mt-0.5">{stat.label}</p>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>

            </div>
        </section>
    )
}

export default PageHeader